import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Save,
  Preview,
  Check,
} from '@mui/icons-material';

interface StepNavigationProps {
  currentStep: number;
  totalSteps: number;
  onNext?: () => void;
  onBack?: () => void;
  onSave?: () => void;
  nextLabel?: string;
  backLabel?: string;
  canGoNext?: boolean;
  canGoBack?: boolean;
  isLoading?: boolean;
  showProgress?: boolean;
  autoSave?: boolean;
  autoSaveStatus?: 'idle' | 'saving' | 'saved' | 'error';
  color?: 'primary' | 'success' | 'warning' | 'error';
  showNext?: boolean;
}

const StepNavigation: React.FC<StepNavigationProps> = ({
  currentStep,
  totalSteps,
  onNext,
  onBack,
  onSave,
  nextLabel,
  backLabel,
  canGoNext = true,
  canGoBack = true,
  isLoading = false,
  showProgress = true,
  autoSave = false,
  autoSaveStatus = 'idle',
  color = 'primary',
  showNext = true
}) => {
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;
  const progressPercentage = ((currentStep + 1) / totalSteps) * 100;

  const getNextButtonText = () => {
    if (nextLabel) return nextLabel;
    if (isLastStep) return 'Dokončit';
    return 'Pokračovat';
  };

  const getNextButtonIcon = () => {
    if (isLastStep) return <Check />;
    return <ArrowForward />;
  };

  const getAutoSaveStatus = () => {
    switch (autoSaveStatus) {
      case 'saving':
        return { text: 'Ukládám změny...', color: 'info.main' };
      case 'saved':
        return { text: 'Uloženy poslední změny', color: 'success.main' };
      case 'error':
        return { text: 'Chyba při ukládání změn', color: 'error.main' };
      default:
        return null;
    }
  };

  const autoSaveInfo = getAutoSaveStatus();

  return (
    <Box sx={{ px: { xs: 3, sm: 4 } }}>
      {/* Progress header – softened, rounded container */}
      {showProgress && (
        <Box
          sx={{
            mb: 3,
            p: { xs: 2.5, sm: 3 },
            borderRadius: '16px',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f7f7f9 0%, #f1f2f6 100%)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.07)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{
              px: 1.25,
              py: 0.25,
              borderRadius: 999,
              bgcolor: 'rgba(0,0,0,0.06)'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Krok {currentStep + 1} z {totalSteps}
              </Typography>
            </Box>
            <Box sx={{
              px: 1.25,
              py: 0.25,
              borderRadius: 999,
              bgcolor: 'rgba(0,0,0,0.06)'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {Math.round(progressPercentage)}% dokončeno
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 12,
              mt: 1.25,
              mb: 0.5,
              borderRadius: 999,
              backgroundColor: 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                background: `linear-gradient(135deg, ${getColorGradient(color)})`,
              },
            }}
          />
        </Box>
      )}

      {/* Auto-save status */}
      {autoSave && autoSaveInfo && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Save fontSize="small" sx={{ color: autoSaveInfo.color }} />
          <Typography variant="caption" sx={{ color: autoSaveInfo.color }}>
            {autoSaveInfo.text}
          </Typography>
        </Box>
      )}

      {/* Navigation buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: 2,
        pt: 2,
        // Zjemnění oddělovače – bez tvrdé linky
        // borderTop: '1px solid rgba(0,0,0,0.08)'
      }}>
        {/* Back button */}
        <Box>
          {!isFirstStep && (
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={onBack}
              disabled={!canGoBack || isLoading}
              sx={{
                borderRadius: 3,
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 0.8, sm: 1, md: 1.2 },
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                minWidth: { xs: 80, sm: 100, md: 120 },
                borderColor: canGoBack && !isLoading ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.06)',
                color: canGoBack && !isLoading ? 'text.secondary' : 'rgba(0, 0, 0, 0.26)',
                backgroundColor: canGoBack && !isLoading ? 'transparent' : 'rgba(0,0,0,0.02)',
                '&:hover': canGoBack && !isLoading ? {
                  borderColor: 'rgba(0,0,0,0.2)',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                } : {
                  backgroundColor: 'rgba(0,0,0,0.02)',
                },
                '&:disabled': {
                  opacity: 1,
                  cursor: 'not-allowed',
                },
              }}
            >
              {backLabel || 'Zpět'}
            </Button>
          )}
        </Box>

        {/* Right side buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Manual save button (if not auto-save) */}
          {!autoSave && onSave && (
            <Tooltip title="Uložit rozpracovaný formulář">
              <IconButton
                onClick={onSave}
                disabled={isLoading}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                <Save />
              </IconButton>
            </Tooltip>
          )}

          {/* Next/Finish button */}
          {showNext && (
            <Button
              variant="contained"
              endIcon={isLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : getNextButtonIcon()}
              onClick={onNext}
              disabled={!canGoNext || isLoading}
              sx={{
                borderRadius: 3,
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 0.8, sm: 1, md: 1.2 },
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                fontWeight: 600,
                textTransform: 'none',
                minWidth: { xs: 100, sm: 120, md: 140 },
                boxShadow: canGoNext && !isLoading ? '0 8px 25px rgba(0,0,0,0.15)' : 'none',
                background: canGoNext && !isLoading 
                  ? `linear-gradient(135deg, ${getColorGradient(color)})` 
                  : 'linear-gradient(135deg, #e0e0e0, #bdbdbd)',
                color: canGoNext && !isLoading ? 'white' : 'rgba(0, 0, 0, 0.38)',
                '&:hover': canGoNext && !isLoading ? {
                  boxShadow: '0 12px 35px rgba(0,0,0,0.2)',
                  transform: 'translateY(-2px)',
                  background: `linear-gradient(135deg, ${getColorGradient(color)})`,
                } : {
                  background: 'linear-gradient(135deg, #e0e0e0, #bdbdbd)',
                },
                '&:disabled': {
                  opacity: 1,
                  transform: 'none',
                  cursor: 'not-allowed',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isLoading ? 'Generuji...' : getNextButtonText()}
            </Button>
          )}
        </Box>
      </Box>

    </Box>
  );
};

const getColorGradient = (color: string): string => {
  switch (color) {
    case 'primary':
      return '#1976d2, #1565c0';
    case 'success':
      return '#388e3c, #2e7d32';
    case 'warning':
      return '#f57c00, #ef6c00';
    case 'error':
      return '#d32f2f, #c62828';
    default:
      return '#1976d2, #1565c0';
  }
};

export default StepNavigation;
